import { createContext, useState, useRef } from "react";
import { Action, ObjectType, defaultBlue } from "../data/constants";
import { useUndoRedo, useTransform, useSelect } from "../hooks";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getDrawDBApp } from "ee/selectors/drawdbSelectors";
import { editDrawDB } from "ee/actions/drawdbActions";
import { debounce } from "lodash";

export const AreasContext = createContext(null);

export default function AreasContextProvider({ children }) {
  const { t } = useTranslation();
  const [areas, setAreas] = useState([]);
  const { transform } = useTransform();
  const { selectedElement, setSelectedElement } = useSelect();
  const { setUndoStack, setRedoStack } = useUndoRedo();
  const dispatch = useDispatch();
  const drawDBApp = useSelector(getDrawDBApp);

  const debouncedDispatch = useRef(
    debounce((action) => {
      dispatch(action);
    }, 300),
  ).current;

  const addArea = (data, addToHistory = true) => {
    if (data) {
      setAreas((prev) => {
        const temp = prev.slice();
        temp.splice(data.id, 0, data);
        const areas = temp.map((t, i) => ({ ...t, id: i }));
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, areas },
          }),
        );
        return areas;
      });
    } else {
      const width = 200;
      const height = 200;
      setAreas((prev) => {
        const areas = [
          ...prev,
          {
            id: prev.length,
            name: `area_${prev.length}`,
            x: transform.pan.x - width / 2,
            y: transform.pan.y - height / 2,
            width,
            height,
            color: "#EEE7FF",
          },
        ];
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, areas },
          }),
        );
        return areas;
      });
    }
    if (addToHistory) {
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.ADD,
          element: ObjectType.AREA,
          message: t("add_area"),
        },
      ]);
      setRedoStack([]);
    }
  };

  const deleteArea = (id, addToHistory = true) => {
    if (addToHistory) {
      message.success(t("area_deleted"));
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.DELETE,
          element: ObjectType.AREA,
          data: areas[id],
          message: t("delete_area", areas[id].name),
        },
      ]);
      setRedoStack([]);
    }
    setAreas((prev) => {
      const areas = prev
        .filter((e) => e.id !== id)
        .map((e, i) => ({ ...e, id: i }));
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, areas },
        }),
      );
      return areas;
    });
    if (id === selectedElement.id) {
      setSelectedElement((prev) => ({
        ...prev,
        element: ObjectType.NONE,
        id: -1,
        open: false,
      }));
    }
  };

  const updateArea = (id, values) => {
    setAreas((prev) => {
      const areas = prev.map((t) => {
        if (t.id === id) {
          return {
            ...t,
            ...values,
          };
        }
        return t;
      });
      // debouncedDispatch(
      //   editDrawDB({
      //     id: drawDBApp.id,
      //     layout: { ...drawDBApp.layout, areas },
      //   }),
      // );
      return areas;
    });
  };

  return (
    <AreasContext.Provider
      value={{
        areas,
        setAreas,
        updateArea,
        addArea,
        deleteArea,
        areasCount: areas.length,
      }}
    >
      {children}
    </AreasContext.Provider>
  );
}
